import type { RefObject, SubmitEventHandler } from 'react';
import type { SwitchChangeFn } from '../../components/Switch/Switch';
import type { SetFormApiFn } from '../../form-hooks/form-components/FormStateHandler/FormStateHandler';
import type { FormError } from '../../form-hooks/lib/helpers';
import type { NewMediaUploadFn } from '../../hooks/api/useMediaUpload';
import type {
  ContentTypeDefinition,
  SlugRule,
  WithOptionalInternalProps,
  WithoutInternalProps,
} from '../../lib/flotiq-client/types/api-response';
import type { ContentTypeFormProperty } from '../PropertyModalForm/helpers/parseProperty';
import type { DraggableCardEditFn } from './DraggableCard/DraggableCard';

import { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import slug from 'slug';

import { showValidationErrorToast } from '../../form-hooks/lib/helpers';
import { getEnv } from '../../lib/env';
import { formatErrorToString, getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import Switch from '../../components/Switch/Switch';
import Tooltip from '../../components/Tooltip/Tooltip';

import useDebounceCallback from '../../hooks/useDebounceCallback';
import useSelectedSpace from '../../hooks/useSelectedSpace';

import ContentTypeFormContext from '../../contexts/ContentTypeFormContext';
import {
  defaultButtonsProps,
  defaultTextRender,
  useModals,
} from '../../contexts/ModalContext';

import { QuestionMarkCircleIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';
import { EDITABLE } from '../PropertyModalForm/helpers/propertiesFields';
import PropertyModalForm from '../PropertyModalForm/PropertyModalForm';
import SlugSettingsModalContent from '../SlugSettingsModalContent/SlugSettingsModalContent';
import DraggableCard from './DraggableCard/DraggableCard';
import {
  changeOrderDown,
  changeOrderUp,
  deleteProperty,
  updateOrder,
  updateProperties,
} from './helpers/parser';
import { removeNulls } from './helpers/submit-transform';
import { generateZodSchema } from './helpers/zod';

type CtdFormErrors = {
  co?: Record<string, string[]>;
  ctd?: Record<string, string[]>;
} & { [key: string]: string | string[] };

const parsePropErrors = (
  errors: Record<string, string | string[]>,
  newPropertiesErrors: Record<string, Record<string, string[]>>,
  key: string,
  findRegexp: RegExp,
  group: string,
) => {
  const propsErrors = Object.keys(errors).filter((errorKey) =>
    errorKey.includes(key),
  );
  if (propsErrors.length) {
    propsErrors.forEach((errorKey) => {
      const { propName, prop } = errorKey.match(findRegexp)?.groups || {};
      if (!prop) {
        newPropertiesErrors[propName] = {
          ...newPropertiesErrors[propName],
          global: [
            ...(newPropertiesErrors[propName]?.global || []),
            ...(Array.isArray(errors[errorKey])
              ? errors[errorKey]
              : [errors[errorKey]]),
          ],
        };
      } else {
        newPropertiesErrors[propName] = {
          ...newPropertiesErrors[propName],
          form: {
            ...newPropertiesErrors[propName]?.form,
            ...(prop === 'required'
              ? { [prop]: errors[errorKey] }
              : { [`${group}.${prop}`]: errors[errorKey] }),
          },
        };
      }
    });
  }
};

export type ContentTypeFormSubmitFn = (
  value: WithoutInternalProps<ContentTypeDefinition>,
) => Promise<
  [
    WithOptionalInternalProps<ContentTypeDefinition>,
    CtdFormErrors | null,
    boolean,
  ]
>;

interface ContentTypeFormProps {
  /**
   * On submit callback
   */
  onSubmit: ContentTypeFormSubmitFn;
  /**
   * Content type to edit
   */
  contentType?: WithOptionalInternalProps<ContentTypeDefinition> | null;
  /**
   * If form is used for object editing
   */
  isEditing?: boolean;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * If navigates after submit represented by react ref
   */
  navigateOnSave?: RefObject<boolean>;
  /**
   * Callback for media uploading
   */
  onMediaUpload?: NewMediaUploadFn;
  /**
   * Set form state
   */
  setFormState?: SetFormApiFn;
  /**
   * Test id for page
   */
  testId?: string;
}

const ContentTypeForm = ({
  contentType,
  isEditing = false,
  onSubmit,
  disabled = false,
  navigateOnSave,
  onMediaUpload,
  setFormState,
  testId = '',
}: ContentTypeFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modal = useModals();
  const { buildUrlWithSpace } = useSelectedSpace();

  const metaPath = 'metaDefinition';
  const schemaPath = 'schemaDefinition.allOf[1]';
  const requiredBasePath = 'schemaDefinition';

  const [fieldsToDelete, setFieldsToDelete] = useState<string[]>([]);
  const oldOrder = useMemo(
    () => contentType?.metaDefinition?.order || [],
    [contentType],
  );
  const [propertiesErrors, setPropertiesErrors] = useState({});
  const [transformError, setTransformError] = useState<
    string | string[] | Record<string, string[]>
  >();
  const [transformCoError, setTransformCoError] =
    useState<Record<string, string | string[]>>();
  const [dirty, setDirty] = useState(false);

  const validationSchema = useMemo(() => generateZodSchema(t), [t]);

  const isAutoSaveEnabled = getEnv('VITE_AUTO_SAVE_ENABLE') === 'true';

  const initialValues = useMemo(
    () =>
      contentType
        ? JSON.parse(JSON.stringify(contentType))
        : {
            label: '',
            name: '',
            autoSave: false,
            draftPublic: false,
            metaDefinition: { order: [], propertiesConfig: {} },
            schemaDefinition: {
              additionalProperties: false,
              required: [],
              type: 'object',
              allOf: [
                {
                  $ref: '#/components/schemas/AbstractContentTypeSchemaDefinition',
                },
                { type: 'object', properties: {} },
              ],
            },
            slugs: [],
          },
    [contentType],
  );

  const form = useAppForm({
    defaultValues: initialValues as WithoutInternalProps<ContentTypeDefinition>,
    onSubmit: async ({ value, formApi }) => {
      if (fieldsToDelete.length) {
        const result = await modal({
          title: defaultTextRender.warning(t('Global.Warning')),
          content: (
            <Trans
              i18nKey="ContentTypeForm.ConfirmDeleteProperties"
              values={{ properties: fieldsToDelete.join(', ') }}
              components={{ 0: <div className="font-bold inline" /> }}
            />
          ),
          buttons: [
            {
              ...defaultButtonsProps.save,
              label: t('Global.Save'),
              result: true,
            },
            {
              ...defaultButtonsProps.cancel,
              label: t('Global.Cancel'),
              result: false,
            },
          ],
        });
        if (!result) return;
      }

      setDirty(false);
      removeNulls(value);
      const [newContentType, errors, hasErrors] = await onSubmit(value);

      if (!hasErrors) {
        setFieldsToDelete([]);
        setPropertiesErrors({});
        setTransformCoError(undefined);
        setTransformError(undefined);

        if (navigateOnSave?.current) {
          navigate(buildUrlWithSpace(`content-type-definitions`));
        }

        form.reset(newContentType);
      } else {
        setDirty(true);
        if (!errors || !Object.keys(errors).length) return;
        const newPropertiesErrors = {};
        parsePropErrors(
          errors,
          newPropertiesErrors,
          'metaDefinition.propertiesConfig',
          /metaDefinition\.propertiesConfig[.[](?<propName>[^.\]]*)]?(?:\.(?<prop>[^.]+))?/,
          'config',
        );
        parsePropErrors(
          errors,
          newPropertiesErrors,
          'schemaDefinition.allOf[1].properties',
          /schemaDefinition\.allOf\[1\]\.properties[.[](?<propName>[^.\]]*)]?(?:\.(?<prop>[^.]+))?/,
          'schema',
        );
        setPropertiesErrors(newPropertiesErrors);

        setTransformCoError(errors.co);
        setTransformError(errors.ctd || errors['']);

        const filteredErrors: Record<string, FormError> = {};

        Object.entries(errors).forEach(([key, error]) => {
          if (['label', 'name', 'draftPublic'].includes(key)) {
            formApi.setFieldMeta(
              key as 'label' | 'name' | 'draftPublic',
              (prev) => ({
                ...prev,
                errorMap: { onSubmit: error },
              }),
            );
          } else {
            filteredErrors[key] = error;
          }
        });

        if (Object.keys(filteredErrors).length) {
          showValidationErrorToast(filteredErrors);
        }
      }
    },
    validators: { onChange: validationSchema },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const handleDeleteField = useCallback(
    async (fieldName: string) => {
      const showModal = isEditing && oldOrder.indexOf(fieldName) > -1;

      const result = !showModal
        ? true
        : await modal.delete(t('ContentTypeForm.DeleteProperty'));

      if (result) {
        if (showModal) setFieldsToDelete((prev) => [...prev, fieldName]);

        deleteProperty(
          form,
          fieldName,
          metaPath,
          schemaPath,
          requiredBasePath,
          setDirty,
        );
      }
    },
    [isEditing, oldOrder, modal, t, form],
  );

  const handleChangeOrder = useCallback(
    (sourceName: string, targetName: string) => {
      updateOrder(sourceName, targetName, form, metaPath, setDirty);
    },
    [form],
  );

  const onDropCallback = useDebounceCallback(handleChangeOrder, 10);

  const handleOrderUp = useCallback(
    (idx: number) => {
      changeOrderUp(form, metaPath, idx, setDirty);
    },
    [form],
  );

  const handleOrderDown = useCallback(
    (idx: number) => {
      changeOrderDown(form, metaPath, idx, setDirty);
    },
    [form],
  );

  const updateFormProperties = useCallback(
    (
      property: ContentTypeFormProperty | undefined,
      newProperty: ContentTypeFormProperty,
      copiedPropertyIdx?: number,
    ) => {
      updateProperties(
        form,
        property,
        newProperty,
        copiedPropertyIdx,
        metaPath,
        schemaPath,
        requiredBasePath,
        setDirty,
      );
    },
    [form],
  );

  const ctdContextValue = useMemo(
    () => ({
      oldCtd: contentType,
      setDirty,
      disabled,
      isEditing,
      onMediaUpload,
      propertiesErrors,
    }),
    [contentType, isEditing, onMediaUpload, propertiesErrors, disabled],
  );

  const openPropertyModal = useCallback<DraggableCardEditFn>(
    async (
      fieldName,
      fieldProps,
      fieldSchema,
      isRequired,
      copiedPropertyIdx,
    ) => {
      let property: ContentTypeFormProperty | undefined = undefined;
      if (fieldName && fieldProps && fieldSchema) {
        property = {
          key: fieldName,
          config: fieldProps,
          schema: fieldSchema,
          required: !!isRequired,
        };
      }

      const newProperty = await modal({
        title: (
          <div className="font-bold text-xl sm:text-3xl">
            {t('ContentTypeForm.ContentProperty')}
          </div>
        ),
        content: (
          <ContentTypeFormContext value={ctdContextValue}>
            <PropertyModalForm
              property={property}
              order={form.getFieldValue('metaDefinition.order')}
              isNew={
                (copiedPropertyIdx != null && copiedPropertyIdx > -1) ||
                (!!property?.key && oldOrder.indexOf(property.key) < 0)
              }
              isDuplicate={copiedPropertyIdx != null && copiedPropertyIdx > -1}
              {...getTestProps(testId, 'property-form', 'testId')}
            />
          </ContentTypeFormContext>
        ),
        size: '3xl',
        dialogAdditionalClasses: 'h-full lg:max-h-[calc(100vh-2rem)]',
      });

      if (!newProperty) return;

      updateFormProperties(
        copiedPropertyIdx != null && copiedPropertyIdx > -1
          ? undefined
          : property,
        newProperty,
        copiedPropertyIdx,
      );

      form.validateField('metaDefinition.order', 'change');
    },
    [modal, t, ctdContextValue, form, oldOrder, testId, updateFormProperties],
  );

  const openSlugSettingsModal = useCallback(async () => {
    const propertyNames: string[] =
      form.getFieldValue('metaDefinition.order') ?? [];
    const snapshot = form.getFieldValue('slugs');

    const result = await modal({
      title: (
        <div className="font-bold text-xl sm:text-3xl">
          {t('ContentTypeForm.SlugsSettingsModal.SlugSettings')}
        </div>
      ),
      content: (
        <ContentTypeFormContext value={ctdContextValue}>
          <form.AppForm>
            <form.AppField name="slugs">
              {() => (
                <SlugSettingsModalContent
                  propertyNames={propertyNames}
                  disabled={disabled}
                  testId={testId}
                />
              )}
            </form.AppField>
          </form.AppForm>
        </ContentTypeFormContext>
      ),
      size: 'lg',
      buttons: [
        {
          ...defaultButtonsProps.cancel,
          label: t('Global.Cancel'),
          result: false,
        },
        { ...defaultButtonsProps.save, label: t('Global.Save'), result: true },
      ],
    });

    if (!result) {
      form.setFieldValue('slugs', snapshot);
    }
  }, [modal, t, ctdContextValue, disabled, testId, form]);

  const setSlugsActive = useCallback(
    (active: boolean) =>
      form.setFieldValue('slugs', (prev: SlugRule[]) =>
        prev.map((slugRule) => ({ ...slugRule, active })),
      ),
    [form],
  );

  const handleSlugToggle = useCallback<SwitchChangeFn>(
    async (_, value) => {
      if (!value) {
        setSlugsActive(value);
        return false;
      }
      if (form.getFieldValue('slugs').length === 0) {
        await openSlugSettingsModal();
        return form.getFieldValue('slugs').some((slugRule) => slugRule.active);
      }
      setSlugsActive(value);
      return true;
    },
    [form, setSlugsActive, openSlugSettingsModal],
  );

  const handleEditField = useCallback<DraggableCardEditFn>(
    async (
      fieldName,
      fieldProps,
      fieldSchema,
      isRequired,
      copiedPropertyIdx = -1,
    ) => {
      if (fieldProps?.inputType && EDITABLE.indexOf(fieldProps.inputType) < 0) {
        alert('Not implemented');
        return;
      }
      await openPropertyModal(
        fieldName,
        fieldProps,
        fieldSchema,
        isRequired,
        copiedPropertyIdx,
      );
    },
    [openPropertyModal],
  );

  const renderField = useCallback(
    (fieldName: string, idx: number, currentOrder: string[], values: any) => (
      <DraggableCard
        key={fieldName}
        form={form}
        fields={{
          metaDefinition: 'metaDefinition',
          schemaDefinition: 'schemaDefinition',
        }}
        fieldName={fieldName}
        idx={idx}
        currentOrder={currentOrder}
        fieldProps={values.metaDefinition?.propertiesConfig?.[fieldName]}
        fieldSchema={
          values.schemaDefinition?.allOf?.[1]?.properties?.[fieldName]
        }
        isRequired={values.schemaDefinition?.required.indexOf(fieldName) > -1}
        onDelete={handleDeleteField}
        onEdit={handleEditField}
        onDrop={onDropCallback}
        onUp={handleOrderUp}
        onDown={handleOrderDown}
        onDuplicate={handleEditField}
        testId={testId}
      />
    ),
    [
      form,
      handleDeleteField,
      handleEditField,
      onDropCallback,
      handleOrderUp,
      handleOrderDown,
      testId,
    ],
  );

  return (
    <ContentTypeFormContext value={ctdContextValue}>
      <form.AppForm>
        <form
          id="ctd-form"
          className="space-y-2 md:space-y-4 p-5 md:py-10 md:px-12"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="flex flex-col sm:flex-row gap-2 lg:gap-10">
            <form.AppField
              name="label"
              listeners={{
                onBlur: ({ value, fieldApi }) => {
                  if (fieldApi.form.getFieldValue('name')) return;
                  fieldApi.form.setFieldValue(
                    'name',
                    slug(value, {
                      replacement: '_',
                      charmap: { '/': '_' },
                    }).replace(/\d/g, ''),
                  );
                  fieldApi.form.validateField('name', 'change');
                },
              }}
            >
              {(field) => (
                <field.Input
                  label={t('ContentDefinition.Label')}
                  disabled={disabled}
                  additionalClasses="max-w-2xl"
                  helpText={
                    <div className="flex">
                      <QuestionMarkCircleIcon className="text-blue w-3 ml-1  mr-1" />
                      {t('Form.FormCreateFolderHelpText')}
                    </div>
                  }
                  visualSeparateSlash={true}
                  required
                  {...getTestProps(testId, 'label', 'testId')}
                />
              )}
            </form.AppField>

            {/**
             * There are problems that main validation has no actual data of this field,
             * due to the fact that it is set programmatically.
             */}
            <form.AppField
              name="name"
              validators={{
                onChange: ({ value }) => {
                  if (!value) return t('Form.FormErrorNotBlank');
                  if (!/^[a-zA-Z_]*$/.test(value)) {
                    return t('ContentTypeForm.Errors.ApiKey');
                  }
                },
              }}
            >
              {(field) => (
                <field.Input
                  label={
                    <Tooltip
                      tooltip={t('ContentTypeForm.ApiNameTooltip')}
                      additionalClasses="w-fit inline-flex"
                      phoneTooltipPlacement={'rightCenter'}
                    >
                      {t('ContentTypeForm.Name')}
                      <QuestionMarkCircleIcon className="text-blue w-3 ml-1" />
                    </Tooltip>
                  }
                  additionalClasses="max-w-2xl"
                  disabled={isEditing || disabled}
                  required
                  {...getTestProps(testId, 'name', 'testId')}
                />
              )}
            </form.AppField>
          </div>

          {transformError && (
            <div
              className="text-red"
              {...getTestProps(testId, 'ctd-transform-errors')}
            >
              {formatErrorToString(transformError)}
            </div>
          )}
          {transformCoError && (
            <div
              className="flex flex-col text-red"
              {...getTestProps(testId, 'co-transform-errors')}
            >
              <span className="font-bold mb-2">
                {t('ContentDefinition.Transformation.Errors')}
              </span>
              <span>{t('ContentDefinition.Transformation.Description')}</span>
              <span className="whitespace-pre-line">
                {Object.keys(transformCoError)
                  .filter((key) => key)
                  .map((key) => `-${key}: ${transformCoError[key]} \n`)}
              </span>
            </div>
          )}

          <div className="flex flex-col gap-8 py-3 sm:gap-10 md:gap-14 sm:flex-row">
            <Tooltip
              tooltip={t('ContentTypeForm.DraftPublicHelpText')}
              additionalClasses="flex"
            >
              <form.AppField name="draftPublic">
                {(field) => (
                  <field.Switch
                    label={t('ContentTypeForm.DraftPublic')}
                    disabled={disabled}
                    additionalClasses="justify-items-start"
                    size="large"
                    {...getTestProps(testId, 'draftPublic', 'testId')}
                  />
                )}
              </form.AppField>
              <QuestionMarkCircleIcon className="text-blue w-3 ml-2" />
            </Tooltip>

            {isAutoSaveEnabled && (
              <Tooltip
                tooltip={t('ContentTypeForm.AutoSaveHelpText')}
                additionalClasses="flex"
              >
                <form.AppField name="autoSave">
                  {(field) => (
                    <field.Switch
                      label={t('ContentTypeForm.AutoSave')}
                      disabled={disabled}
                      additionalClasses="justify-items-start"
                      size="large"
                      {...getTestProps(testId, 'autoSave', 'testId')}
                    />
                  )}
                </form.AppField>
                <QuestionMarkCircleIcon className="text-blue w-3 ml-2" />
              </Tooltip>
            )}

            <Tooltip
              tooltip={t('ContentTypeForm.GenerateSlugHelpText')}
              additionalClasses="flex"
            >
              <form.Subscribe
                selector={({ values }) =>
                  (values.slugs ?? []).some((slug) => slug.active)
                }
              >
                {(isEnabled) => (
                  <Switch
                    label={t('ContentTypeForm.GenerateSlug')}
                    disabled={disabled}
                    checked={isEnabled}
                    onLabelClick={isEnabled ? openSlugSettingsModal : undefined}
                    onChange={handleSlugToggle}
                    additionalClasses={'justify-items-start'}
                    size="large"
                    {...getTestProps(testId, 'generateSlug', 'testId')}
                  />
                )}
              </form.Subscribe>
              <QuestionMarkCircleIcon className="text-blue w-3 ml-2" />
            </Tooltip>
          </div>

          <form.Subscribe selector={({ values }) => values}>
            {(values) =>
              (values.metaDefinition?.order || []).map(
                (fieldName, idx, currentOrder) =>
                  renderField(fieldName, idx, currentOrder, values),
              )
            }
          </form.Subscribe>

          {/**
           * There are problems that main validation has no actual data of this field,
           * due to the fact that it is set programmatically.
           */}
          <form.AppField
            name="metaDefinition.order"
            validators={{
              onChange: ({ value }) => {
                if (!value?.length)
                  return t('ContentTypeForm.Errors.OneProperty');
              },
            }}
          >
            {(subField) => (
              <subField.ErrorField
                {...getTestProps(testId, 'properties-error', 'testId')}
              />
            )}
          </form.AppField>

          <div className="flex justify-between items-center mt-6">
            <Button
              onClick={() => openPropertyModal()}
              disabled={disabled}
              {...getTestProps(testId, 'add-property', 'testId')}
            >
              {t('ContentTypeForm.AddProperty')}
            </Button>
          </div>

          <form.DirtyHandler dirty={dirty} />
          <form.ValidationToastHandler />
          <form.FormStateHandler setFormState={setFormState} />
        </form>
      </form.AppForm>
    </ContentTypeFormContext>
  );
};

export default ContentTypeForm;
